import React, { useRef, useEffect } from 'react'
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Grid,
  Box
} from '@mui/material'
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import { Base64Amiri } from '../../../../assets/dist/base64Amiri'
import { useTranslation } from 'react-i18next'

const PDFDownload = ({ reports, headerInfo }) => {
  const { t, i18n } = useTranslation()
  const tableRef = useRef(null)

  const downloadPDF = async () => {
    const input = tableRef.current
    const canvas = await html2canvas(input, {
      scale: 2,
      useCORS: true,
      logging: true,
      letterRendering: 1
    })

    const imgData = canvas.toDataURL('image/png')
    const pdf = new jsPDF('l', 'mm', 'a4')

    const amiriFont = Base64Amiri
    const fontName = 'Amiri'

    pdf.addFileToVFS(fontName + '.ttf', amiriFont)
    pdf.addFont(fontName + '.ttf', fontName, 'normal', 'bold')

    pdf.setFont(fontName)
    pdf.setFontSize(20)

    const imgProps = pdf.getImageProperties(imgData)
    const pdfWidth = pdf.internal.pageSize.getWidth()
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width

    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight)

    pdf.save('gass_report.pdf')
  }

  const totalRow = reports.reduce(
    (acc, row) => ({
      total_gross: acc.total_gross + row.total_gross,
      total_net: acc.total_net + row.total_net,
      total_net_vat: acc.total_net_vat + row.total_net_vat,
      total_discount: acc.total_discount + row.total_discount,
      total_patient_share: acc.total_patient_share + row.total_patient_share,
      total_net_payable: acc.total_net_payable + row.total_net_payable,
      total_taxable: acc.total_taxable + row.total_taxable,
      total_non_taxable: acc.total_non_taxable + row.total_non_taxable
    }),
    {
      total_gross: 0,
      total_net: 0,
      total_net_vat: 0,
      total_discount: 0,
      total_patient_share: 0,
      total_net_payable: 0,
      total_taxable: 0,
      total_non_taxable: 0
    }
  )

  return (
    <div>
      <div className='flex justify-end my-6'>
        <Button
          variant='contained'
          color='primary'
          onClick={downloadPDF}
          sx={{ mr: 2 }}
        >
          {t('Download PDF')}
        </Button>
      </div>
      <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }} ref={tableRef}>
        <Box sx={{ border: '1px solid #ccc', p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <div className='flex gap-6'>
                <img
                  src='/images/clinic.jpg'
                  alt='Company Logo'
                  style={{
                    width: '150px',
                    height: 'auto',
                    marginBottom: '16px'
                  }}
                />
                <div>
                  <Typography variant='body2' sx={{ display: 'flex', mb: 1 }}>
                    <Box
                      component='span'
                      sx={{ fontWeight: 'bold', width: '180px' }}
                    >
                      {t('To')}:
                    </Box>
                    {headerInfo.to}
                  </Typography>
                  <Typography variant='body2' sx={{ display: 'flex', mb: 1 }}>
                    <Box
                      component='span'
                      sx={{ fontWeight: 'bold', width: '180px' }}
                    >
                      {t('Provider Code')}:
                    </Box>
                    {headerInfo.providerCode}
                  </Typography>
                  <Typography variant='body2' sx={{ display: 'flex', mb: 1 }}>
                    <Box
                      component='span'
                      sx={{ fontWeight: 'bold', width: '180px' }}
                    >
                      {t('Provider Name')}:
                    </Box>
                    {headerInfo.providerName}
                  </Typography>
                  <Typography variant='body2' sx={{ display: 'flex', mb: 1 }}>
                    <Box
                      component='span'
                      sx={{ fontWeight: 'bold', width: '180px' }}
                    >
                      {t('Total Number Of Claim')}:
                    </Box>
                    {headerInfo.totalNumberOfClaim}
                  </Typography>
                </div>
              </div>
            </Grid>
            <Grid item xs={6}>
              <Typography variant='body2' sx={{ display: 'flex', mb: 1 }}>
                <Box
                  component='span'
                  sx={{ fontWeight: 'bold', width: '180px' }}
                >
                  {t('Status')}:
                </Box>
                {headerInfo.status}
              </Typography>
              <Typography variant='body2' sx={{ display: 'flex', mb: 1 }}>
                <Box
                  component='span'
                  sx={{ fontWeight: 'bold', width: '180px' }}
                >
                  {t('Batch Month')}:
                </Box>
                {headerInfo.batchMonth}
              </Typography>
              <Typography variant='body2' sx={{ display: 'flex', mb: 1 }}>
                <Box
                  component='span'
                  sx={{ fontWeight: 'bold', width: '180px' }}
                >
                  {t('Date & Time')}:
                </Box>
                {headerInfo.date}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Typography
          variant='h6'
          gutterBottom
          component='div'
          className='text-center'
        >
          {headerInfo?.payerName
            ? headerInfo.payerName
            : t('Report for Selected Payer')}
        </Typography>

        <TableContainer>
          <Table stickyHeader aria-label='sticky table'>
            <TableHead>
              <TableRow>
                <TableCell>{t('Policy Holder')}</TableCell>
                <TableCell>{t('Total Gross')}</TableCell>
                <TableCell>{t('Total Net')}</TableCell>
                <TableCell>{t('Total VAT')}</TableCell>
                <TableCell>{t('Total Discount')}</TableCell>
                <TableCell>{t('Total Patient Share')}</TableCell>
                <TableCell>{t('Total Payable')}</TableCell>
                <TableCell>{t('Total Taxable')}</TableCell>
                <TableCell>{t('Total Non Taxable')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((report, index) => (
                <TableRow key={index}>
                  <TableCell className='noto-sans-arabic'>
                    {report.policy_holder || 'N/A'}
                  </TableCell>
                  <TableCell>{report.total_gross?.toFixed(2)}</TableCell>
                  <TableCell>{report.total_net?.toFixed(2)}</TableCell>
                  <TableCell>{report.total_net_vat?.toFixed(2)}</TableCell>
                  <TableCell>{report.total_discount?.toFixed(2)}</TableCell>
                  <TableCell>
                    {report.total_patient_share?.toFixed(2)}
                  </TableCell>
                  <TableCell>{report.total_net_payable?.toFixed(2)}</TableCell>
                  <TableCell>{report.total_taxable?.toFixed(2)}</TableCell>
                  <TableCell>{report.total_non_taxable?.toFixed(2)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell className='!flex justify-end'>
                  <strong>{t('Total')}</strong>
                </TableCell>
                <TableCell>
                  <strong>{totalRow.total_gross?.toFixed(2)}</strong>
                </TableCell>
                <TableCell>
                  <strong>{totalRow.total_net?.toFixed(2)}</strong>
                </TableCell>
                <TableCell>
                  <strong>{totalRow.total_net_vat?.toFixed(2)}</strong>
                </TableCell>
                <TableCell>
                  <strong>{totalRow.total_discount?.toFixed(2)}</strong>
                </TableCell>
                <TableCell>
                  <strong>{totalRow.total_patient_share?.toFixed(2)}</strong>
                </TableCell>
                <TableCell>
                  <strong>{totalRow.total_net_payable?.toFixed(2)}</strong>
                </TableCell>
                <TableCell>
                  <strong>{totalRow.total_taxable?.toFixed(2)}</strong>
                </TableCell>
                <TableCell>
                  <strong>{totalRow.total_non_taxable?.toFixed(2)}</strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  )
}

export default PDFDownload
